import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ChevronLeft, Car, AlertCircle, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import Button from "@/components/Button";
import { Field, NativeSelect, FileField } from "@/components/Form";
import { useAuth, routeForRole } from "@/lib/auth";
import { api } from "@/lib/api";
import { MAKE_LIST, VEHICLE_MAKES, VEHICLE_YEARS, DRIVER_AGREEMENT_SECTIONS, DRIVER_AGREEMENT_VERSION } from "@/lib/vehicles";

const DRIVER_STEPS = ["Account", "Vehicle", "Documents", "Agreement"];
const CUSTOMER_STEPS = ["Your details", "Profile photo"];

function ageFromDob(s) {
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const mm = +m[1], dd = +m[2], yyyy = +m[3];
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
  const now = new Date();
  let age = now.getFullYear() - yyyy;
  if (now.getMonth() + 1 < mm || (now.getMonth() + 1 === mm && now.getDate() < dd)) age--;
  return age;
}
const dobToISO = (s) => {
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  return m ? `${m[3]}-${m[1]}-${m[2]}` : undefined;
};
const fmtDob = (raw) => {
  const d = raw.replace(/\D/g, "").slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};
const fmtSsn = (raw) => {
  const d = raw.replace(/\D/g, "").slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
};

export default function Auth() {
  const navigate = useNavigate();
  const { signIn, signUp } = useAuth();
  const [params] = useSearchParams();

  const [mode, setMode] = useState("login");
  const [role, setRole] = useState("customer");
  const [step, setStep] = useState(0);
  const [f, setF] = useState({
    firstName: "", lastName: "", email: "", password: "", phone: "", dob: "",
    vehicleMake: "", vehicleModel: "", vehicleYear: "", vehicleColor: "", plate: "",
    licenseNumber: "", ssn: "", agreedTerms: false,
  });
  const [photo, setPhoto] = useState(null);
  const [licenseDoc, setLicenseDoc] = useState(null);
  const [insuranceDoc, setInsuranceDoc] = useState(null);
  const [registrationDoc, setRegistrationDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emailVerified, setEmailVerified] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [code, setCode] = useState("");
  const [codeBusy, setCodeBusy] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [codeErr, setCodeErr] = useState(null);
  const [showForgot, setShowForgot] = useState(false);
  const [fpStep, setFpStep] = useState(0);
  const [fpEmail, setFpEmail] = useState("");
  const [fpCode, setFpCode] = useState("");
  const [fpPw, setFpPw] = useState("");
  const [fpBusy, setFpBusy] = useState(false);
  const [fpErr, setFpErr] = useState(null);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const openForgot = () => { setShowForgot(true); setFpStep(0); setFpEmail(f.email.trim()); setFpCode(""); setFpPw(""); setFpErr(null); };
  const sendReset = async () => {
    if (!fpEmail.trim()) return setFpErr("Enter your account email.");
    setFpBusy(true); setFpErr(null);
    try {
      await api("/auth/forgot-password", { method: "POST", body: { email: fpEmail.trim().toLowerCase() } });
      setFpStep(1);
    } catch (e) { setFpErr(e.message || "Could not send reset code."); }
    finally { setFpBusy(false); }
  };
  const doReset = async () => {
    if (fpCode.trim().length !== 6) return setFpErr("Enter the 6-digit code.");
    if (fpPw.length < 8) return setFpErr("Password must be at least 8 characters.");
    setFpBusy(true); setFpErr(null);
    try {
      await api("/auth/reset-password", { method: "POST", body: { email: fpEmail.trim().toLowerCase(), code: fpCode.trim(), new_password: fpPw } });
      toast.success("Password updated. Please sign in.");
      setShowForgot(false); set("email", fpEmail.trim()); set("password", "");
    } catch (e) { setFpErr(e.message || "Could not reset password."); }
    finally { setFpBusy(false); }
  };

  const startVerify = async () => {
    setSendingCode(true); setError(null); setCodeErr(null);
    try {
      await api("/auth/send-code", { method: "POST", body: { email: f.email.trim().toLowerCase() } });
      setShowCode(true);
    } catch (e) { setError(e.message || "Could not send verification code."); }
    finally { setSendingCode(false); }
  };
  const verifyCode = async () => {
    setCodeBusy(true); setCodeErr(null);
    try {
      await api("/auth/verify-code", { method: "POST", body: { email: f.email.trim().toLowerCase(), code: code.trim() } });
      setEmailVerified(true); setShowCode(false); setCode("");
      setStep(1);
    } catch (e) { setCodeErr(e.message || "Invalid code."); }
    finally { setCodeBusy(false); }
  };
  const isDriverSignup = mode === "signup" && role === "driver";
  const isCustomerSignup = mode === "signup" && role === "customer";
  const steps = isDriverSignup ? DRIVER_STEPS : CUSTOMER_STEPS;
  const fullName = `${f.firstName.trim()} ${f.lastName.trim()}`.trim();
  const modelOptions = f.vehicleMake ? VEHICLE_MAKES[f.vehicleMake] || [] : [];

  useEffect(() => {
    if (params.get("role") === "driver") {
      setRole("driver");
      setMode("signup");
      setStep(0);
    }
  }, [params]);

  const done = (user) => {
    navigate(routeForRole(user.role), { replace: true });
  };

  const doRegister = async () => {
    setLoading(true);
    try {
      const user = await signUp({
        email: f.email.trim(),
        password: f.password,
        name: fullName,
        first_name: f.firstName.trim() || undefined,
        last_name: f.lastName.trim() || undefined,
        role,
        phone: f.phone.trim() || undefined,
        date_of_birth: role === "customer" ? dobToISO(f.dob) : undefined,
        photo: photo?.dataUrl,
        vehicle_make: role === "driver" ? f.vehicleMake.trim() : undefined,
        vehicle_model: role === "driver" ? f.vehicleModel.trim() : undefined,
        vehicle_year: role === "driver" ? f.vehicleYear.trim() : undefined,
        vehicle_color: role === "driver" ? f.vehicleColor.trim() : undefined,
        plate: role === "driver" ? f.plate.trim() : undefined,
        license_number: role === "driver" ? f.licenseNumber.trim() : undefined,
        ssn: role === "driver" ? f.ssn.replace(/\D/g, "") : undefined,
        agreed_terms: role === "driver" ? f.agreedTerms : undefined,
        license_doc: role === "driver" ? licenseDoc?.dataUrl : undefined,
        insurance_doc: role === "driver" ? insuranceDoc?.dataUrl : undefined,
        registration_doc: role === "driver" ? registrationDoc?.dataUrl : undefined,
      });
      done(user);
    } catch (e) {
      setError(e.message || "Authentication failed.");
      setLoading(false);
    }
  };

  const submit = async () => {
    setError(null);
    if (mode === "login") {
      if (!f.email || !f.password) return setError("Please fill in all required fields.");
      setLoading(true);
      try { done(await signIn(f.email.trim(), f.password)); }
      catch (e) { setError(e.message || "Authentication failed."); setLoading(false); }
      return;
    }
    if (isCustomerSignup) {
      if (step === 0) {
        if (!f.firstName || !f.lastName || !f.email || !f.password) return setError("Please fill in all required fields.");
        if (f.phone.trim().length < 7) return setError("Please enter a valid phone number.");
        const age = ageFromDob(f.dob);
        if (age === null) return setError("Please enter your date of birth as MM/DD/YYYY.");
        if (age < 18) return setError("You must be at least 18 years old to use Getaride.");
        if (!emailVerified) return startVerify();
        return setStep(1);
      }
      if (!photo) return setError("Please add a profile photo.");
      return doRegister();
    }
    // driver
    if (step === 0) {
      if (!f.firstName || !f.lastName || !f.email || !f.password) return setError("Please fill in your account details.");
      if (!photo) return setError("Please add a profile photo.");
      if (!emailVerified) return startVerify();
      return setStep(1);
    }
    if (step === 1) {
      if (!f.vehicleMake || !f.vehicleModel || !f.vehicleYear || !f.plate) return setError("Please complete your vehicle details.");
      return setStep(2);
    }
    if (step === 2) {
      if (!f.licenseNumber) return setError("Please enter your driver's license number.");
      if (f.ssn.replace(/\D/g, "").length !== 9) return setError("Please enter a valid 9-digit Social Security Number.");
      if (!licenseDoc || !insuranceDoc || !registrationDoc) return setError("Please upload your license, insurance and registration documents.");
      return setStep(3);
    }
    if (!f.agreedTerms) return setError("You must accept the Driver Agreement to submit your application.");
    doRegister();
  };

  const switchMode = () => {
    setMode(mode === "login" ? "signup" : "login");
    setRole("customer");
    setStep(0);
    setError(null);
  };

  let submitLabel = "Sign In";
  if (mode === "signup") {
    if (isDriverSignup) submitLabel = step < 3 ? "Continue" : "Submit Application";
    else submitLabel = step < 1 ? "Continue" : "Create Account";
  }

  const subtitle = mode === "login"
    ? "Welcome back. Sign in to continue."
    : isDriverSignup ? "Become a Getaride driver in 4 quick steps."
    : "Create your rider account to get moving.";

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-b from-accent/40 to-background">
      <header className="flex items-center px-5 py-4">
        <button onClick={() => navigate("/")} data-testid="logo-home"><Logo size={32} /></button>
      </header>
      <div className="flex flex-1 items-start justify-center px-4 pb-10 sm:items-center">
      <div className="w-full max-w-md rounded-3xl bg-card p-6 animate-fade-up sm:border sm:border-border sm:p-7 sm:shadow-card">
        <div className="mb-6 flex flex-col items-center gap-2 text-center">
          <h1 className="text-2xl font-extrabold text-foreground">Getaride Orlando</h1>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>

        {(isDriverSignup || isCustomerSignup) && (
          <div className="mb-6" data-testid="signup-progress">
            <div className="mb-1.5 flex items-center justify-between text-xs font-semibold">
              <span className="text-brand-600">{steps[step]}</span>
              <span className="text-muted-foreground">Step {step + 1} of {steps.length}</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
              <div
                className="h-full rounded-full bg-brand-600 transition-all duration-300"
                style={{ width: `${((step + 1) / steps.length) * 100}%` }}
              />
            </div>
          </div>
        )}

        <div className="space-y-4">
          {(mode === "login" || step === 0) && (
            <>
              {mode === "signup" && (
                <div className="flex gap-3">
                  <Field label="First name" data-testid="first-name-input" value={f.firstName} onChange={(e) => set("firstName", e.target.value)} placeholder="Jane" />
                  <Field label="Last name" data-testid="last-name-input" value={f.lastName} onChange={(e) => set("lastName", e.target.value)} placeholder="Doe" />
                </div>
              )}
              <Field label="Email" data-testid="email-input" type="email" autoCapitalize="none" value={f.email} onChange={(e) => { set("email", e.target.value); setEmailVerified(false); }} placeholder="you@email.com" />
              {mode === "signup" && emailVerified && (
                <p className="-mt-2 flex items-center gap-1.5 text-xs font-semibold text-green-600" data-testid="email-verified">
                  <CheckCircle2 className="h-4 w-4" /> Email verified
                </p>
              )}
              <Field label="Password" data-testid="password-input" type="password" value={f.password} onChange={(e) => set("password", e.target.value)} placeholder="••••••••" />
              {mode === "login" && (
                <button type="button" onClick={openForgot} data-testid="forgot-password" className="-mt-2 block text-right text-sm font-semibold text-brand-600">
                  Forgot password?
                </button>
              )}
              {mode === "signup" && (
                <Field label="Phone number" data-testid="phone-input" value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(407) 555-0123" />
              )}
              {isCustomerSignup && (
                <Field label="Date of birth" data-testid="dob-input" value={f.dob} onChange={(e) => set("dob", fmtDob(e.target.value))} placeholder="MM/DD/YYYY" hint="You must be 18 or older to use Getaride." />
              )}
              {isDriverSignup && (
                <FileField label="Profile photo" hint="a profile photo" imageOnly testID="photo-doc" value={photo} onChange={setPhoto} />
              )}
            </>
          )}

          {isCustomerSignup && step === 1 && (
            <>
              <p className="text-sm text-muted-foreground">Add a clear photo of yourself so your driver can find you.</p>
              <FileField label="Profile photo" hint="a profile photo" imageOnly testID="photo-doc" value={photo} onChange={setPhoto} />
            </>
          )}

          {isDriverSignup && step === 1 && (
            <>
              <NativeSelect label="Make" options={MAKE_LIST} placeholder="Select make" value={f.vehicleMake} onChange={(v) => { set("vehicleMake", v); set("vehicleModel", ""); }} data-testid="make-select" />
              {f.vehicleMake && modelOptions.length > 0 ? (
                <NativeSelect label="Model" options={modelOptions} placeholder="Select model" value={f.vehicleModel} onChange={(v) => set("vehicleModel", v)} data-testid="model-select" />
              ) : (
                <Field label="Model" data-testid="model-input" value={f.vehicleModel} onChange={(e) => set("vehicleModel", e.target.value)} placeholder="Enter model" />
              )}
              <div className="flex gap-3">
                <NativeSelect label="Year" options={VEHICLE_YEARS} placeholder="Year" value={f.vehicleYear} onChange={(v) => set("vehicleYear", v)} data-testid="year-select" />
                <Field label="Color" data-testid="color-input" value={f.vehicleColor} onChange={(e) => set("vehicleColor", e.target.value)} placeholder="Silver" />
              </div>
              <Field label="License plate" data-testid="plate-input" value={f.plate} onChange={(e) => set("plate", e.target.value.toUpperCase())} placeholder="FL 123AB" />
              <p className="text-xs text-muted-foreground">Vehicles must be model year 2010 or newer. Your category is set automatically from your make & model.</p>
            </>
          )}

          {isDriverSignup && step === 2 && (
            <>
              <Field label="Driver's license number" data-testid="license-input" value={f.licenseNumber} onChange={(e) => set("licenseNumber", e.target.value.toUpperCase())} placeholder="D123-456-78-901-0" />
              <Field label="Social Security Number" data-testid="ssn-input" value={f.ssn} onChange={(e) => set("ssn", fmtSsn(e.target.value))} placeholder="123-45-6789" hint="Used only for your background check." />
              <FileField label="Driver's license photo" hint="license" testID="license-doc" value={licenseDoc} onChange={setLicenseDoc} />
              <FileField label="Insurance document" hint="insurance" testID="insurance-doc" value={insuranceDoc} onChange={setInsuranceDoc} />
              <FileField label="Vehicle registration" hint="registration" testID="registration-doc" value={registrationDoc} onChange={setRegistrationDoc} />
            </>
          )}

          {isDriverSignup && step === 3 && (
            <>
              <div className="max-h-64 overflow-y-auto rounded-xl border border-border bg-secondary p-4">
                <h4 className="font-bold text-foreground">Getaride Driver Agreement</h4>
                <p className="text-xs text-muted-foreground">Version {DRIVER_AGREEMENT_VERSION} · Orlando, FL</p>
                {DRIVER_AGREEMENT_SECTIONS.map((s) => (
                  <div key={s.title} className="mt-3">
                    <p className="text-sm font-semibold text-foreground">{s.title}</p>
                    <p className="text-[13px] leading-relaxed text-muted-foreground">{s.body}</p>
                  </div>
                ))}
              </div>
              <button data-testid="agree-checkbox" onClick={() => set("agreedTerms", !f.agreedTerms)} className="flex items-start gap-3 text-left">
                <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 ${f.agreedTerms ? "border-brand-600 bg-brand-600 text-white" : "border-border"}`}>
                  {f.agreedTerms && "✓"}
                </span>
                <span className="text-[13px] text-foreground">I have read and agree to the Getaride Driver Agreement and authorize the background and identity checks described above.</span>
              </button>
            </>
          )}

          {error && (
            <div className="flex items-center gap-2 rounded-xl bg-destructive/10 px-3 py-2.5 text-sm font-medium text-destructive" data-testid="auth-error">
              <AlertCircle className="h-4 w-4 shrink-0" /> {error}
            </div>
          )}

          <div className="flex items-center gap-3">
            {isDriverSignup && step > 0 && (
              <Button variant="outline" size="md" onClick={() => { setStep(step - 1); setError(null); }} data-testid="step-back">
                <ChevronLeft className="h-5 w-5" />
              </Button>
            )}
            <Button className="flex-1" onClick={submit} loading={loading || sendingCode} data-testid="auth-submit">
              {submitLabel}
            </Button>
          </div>
        </div>

        {showCode && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onClick={() => setShowCode(false)}>
            <div className="w-full max-w-sm rounded-3xl bg-card p-6 shadow-card" onClick={(e) => e.stopPropagation()}>
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-accent"><Mail className="h-7 w-7 text-brand-600" /></div>
              <h3 className="text-center text-xl font-extrabold text-foreground">Verify your email</h3>
              <p className="mb-4 mt-1 text-center text-sm text-muted-foreground">We sent a 6-digit code to <span className="font-semibold text-foreground">{f.email}</span>. Enter it below to continue.</p>
              <input
                data-testid="verify-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                placeholder="000000"
                className="mb-3 h-14 w-full rounded-xl border border-border bg-secondary text-center font-num text-2xl font-bold tracking-[0.4em] text-foreground outline-none focus:border-brand-500"
              />
              {codeErr && <p className="mb-2 text-center text-sm font-medium text-destructive" data-testid="verify-error">{codeErr}</p>}
              <Button className="w-full" loading={codeBusy} disabled={code.length !== 6} onClick={verifyCode} data-testid="verify-submit">Verify & continue</Button>
              <button onClick={startVerify} disabled={sendingCode} className="mt-3 w-full text-center text-sm font-semibold text-brand-600" data-testid="resend-code">Resend code</button>
            </div>
          </div>
        )}

        {showForgot && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onClick={() => setShowForgot(false)}>
            <div className="w-full max-w-sm rounded-3xl bg-card p-6 shadow-card" onClick={(e) => e.stopPropagation()} data-testid="forgot-modal">
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-accent"><Mail className="h-7 w-7 text-brand-600" /></div>
              <h3 className="text-center text-xl font-extrabold text-foreground">Reset your password</h3>
              {fpStep === 0 ? (
                <>
                  <p className="mb-4 mt-1 text-center text-sm text-muted-foreground">Enter your account email and we&apos;ll send you a 6-digit reset code.</p>
                  <input data-testid="fp-email" type="email" autoCapitalize="none" value={fpEmail} onChange={(e) => setFpEmail(e.target.value)} placeholder="you@email.com"
                    className="mb-3 h-12 w-full rounded-xl border border-border bg-secondary px-3 text-foreground outline-none focus:border-brand-500" />
                  {fpErr && <p className="mb-2 text-center text-sm font-medium text-destructive" data-testid="fp-error">{fpErr}</p>}
                  <Button className="w-full" loading={fpBusy} onClick={sendReset} data-testid="fp-send">Send reset code</Button>
                </>
              ) : (
                <>
                  <p className="mb-4 mt-1 text-center text-sm text-muted-foreground">We sent a code to <span className="font-semibold text-foreground">{fpEmail}</span>. Enter it and choose a new password.</p>
                  <input data-testid="fp-code" value={fpCode} onChange={(e) => setFpCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="000000"
                    className="mb-3 h-14 w-full rounded-xl border border-border bg-secondary text-center font-num text-2xl font-bold tracking-[0.4em] text-foreground outline-none focus:border-brand-500" />
                  <input data-testid="fp-password" type="password" value={fpPw} onChange={(e) => setFpPw(e.target.value)} placeholder="New password (min 8 chars)"
                    className="mb-3 h-12 w-full rounded-xl border border-border bg-secondary px-3 text-foreground outline-none focus:border-brand-500" />
                  {fpErr && <p className="mb-2 text-center text-sm font-medium text-destructive" data-testid="fp-error">{fpErr}</p>}
                  <Button className="w-full" loading={fpBusy} disabled={fpCode.length !== 6 || fpPw.length < 8} onClick={doReset} data-testid="fp-submit">Update password</Button>
                  <button onClick={sendReset} disabled={fpBusy} className="mt-3 w-full text-center text-sm font-semibold text-brand-600" data-testid="fp-resend">Resend code</button>
                </>
              )}
            </div>
          </div>
        )}

        <button data-testid="toggle-mode" onClick={switchMode} className="mt-6 w-full text-center text-sm text-muted-foreground">
          {mode === "login" ? "New to Getaride? " : "Already have an account? "}
          <span className="font-bold text-brand-600">{mode === "login" ? "Sign up" : "Sign in"}</span>
        </button>

        {role === "driver" ? (
          <button data-testid="back-to-rider" onClick={() => { setRole("customer"); setStep(0); setError(null); }} className="mt-3 flex w-full items-center justify-center gap-1.5 text-sm text-muted-foreground">
            <ChevronLeft className="h-4 w-4" /> Back to rider sign up
          </button>
        ) : (
          <Link to="/drive-with-us" data-testid="become-driver" className="mt-3 flex w-full items-center justify-center gap-1.5 text-sm text-muted-foreground">
            <Car className="h-4 w-4 text-brand-600" /> Want to drive with Getaride? <span className="font-bold text-brand-600">Apply here</span>
          </Link>
        )}
      </div>
      </div>
    </div>
  );
}
